
import { useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemDecorator,
  Divider,
  Stack,
  Chip
} from "@mui/joy";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LaptopIcon from '@mui/icons-material/Laptop';
import WifiIcon from '@mui/icons-material/Wifi';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { Link, useParams } from "react-router";
import useUser from './auth/useUser';
import useTracking from './useTracking';

const VideoInterviewLanding = () => {
  const { clientId, jobId } = useParams();
  const { user } = useUser();
  const isDemo = user?.client?._id === clientId;
  const { trackInterviewInviteAccepted } = useTracking();

  // Find the application for this job
  const application = useMemo(() => {
    if (!user?.talent?.applications) return null;
    return user.talent.applications.find(a => a.job._id === jobId && a.client._id === clientId) || null;
  }, [user, jobId, clientId]);

  const handleStartInterview = () => {
    if (application?._id && jobId && clientId && !isDemo) {
      trackInterviewInviteAccepted(application._id, jobId, clientId);
    }
  };

  return (
    <Box
      sx={{
        // minHeight: '100vh',
        // backgroundColor: 'background.level1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // py: 5,
      }}
    >
      <Card
        sx={{
          maxWidth: 600,
          width: '100%',
          boxShadow: 'lg',
          borderRadius: 'xl',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            {/* Header Section */}
            <Box textAlign="center">
              <Chip
                color="success"
                startDecorator={<CheckCircleIcon />}
                sx={{ mb: 1.5 }}
              >
                Congratulations!
              </Chip>
              <Typography level="h3" sx={{ mb: 0.5, color: 'text.primary' }}>
                You&apos;re invited to the next stage
              </Typography>
            </Box>

            <Divider />

            {/* Main Content */}
            <Box>
              <Typography level="body-md" sx={{ mb: 2, lineHeight: 1.5 }}>
                Based on your experience and responses, we&apos;d love to move forward.
                Please complete our interactive, real-time video interview - a
                <strong> 20–30 minute experience</strong> that lets you demonstrate
                your skills and stand out by sharing your personality and approach.
              </Typography>

              <Typography level="body-sm" sx={{ mb: 1, color: 'text.secondary' }}>
                Our system uses AI to personalize questions and ensure a fair,
                consistent evaluation.
              </Typography>
            </Box>

            {/* Requirements Section */}
            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>
                What you&apos;ll need:
              </Typography>

              <List>
                <ListItem>
                  <ListItemDecorator>
                    <WifiIcon color="primary" />
                  </ListItemDecorator>
                  <Typography level="body-sm">A stable internet connection</Typography>
                </ListItem>
                <ListItem>
                  <ListItemDecorator>
                    <LaptopIcon color="primary" />
                  </ListItemDecorator>
                  <Typography level="body-sm">A laptop or desktop computer</Typography>
                </ListItem>
                <ListItem>
                  <ListItemDecorator>
                    <VolumeOffIcon color="primary" />
                  </ListItemDecorator>
                  <Typography level="body-sm">A quiet space without distractions</Typography>
                </ListItem>
              </List>
            </Box>

            <Divider />

            {/* Call to Action */}
            <Box>
              <Typography level="body-sm" sx={{ mb: 2, mt: 1, textAlign: 'center' }}>
                When you&apos;re ready, click <strong>Start Interview</strong> to begin.
                <br />
                Take your time, be yourself, and show us what makes you a great
                fit for this role.
              </Typography>

              <Button
                size="md"
                startDecorator={<PlayArrowIcon />}
                sx={{
                  width: '100%',
                  py: 1,
                  mt: 2,
                  mb: 1,
                }}
                component={Link}
                to={window.location.href.replace("-intro", "")}
                onClick={handleStartInterview}
              >
                Start Interview Now
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VideoInterviewLanding;
